// Expandable row showing a single evaluation dimension.
// Clicking reveals the reasoning and a quoted example from the paper.

import React, { useState } from 'react';
import type { DimensionScore } from '../types';
import ScoreBar from './ScoreBar';
import resultsStyles from '../styles/Results.module.css';

interface DimensionRowProps {
  label: string;
  data: DimensionScore;
}

// Colors for the jargon assessment badge
const JARGON_STYLES: Record<string, { bg: string; color: string }> = {
  earned: { bg: 'var(--verdict-must-bg)', color: 'var(--verdict-must)' },
  defensive: { bg: 'var(--verdict-skip-bg)', color: 'var(--verdict-skip)' },
  mixed: { bg: 'var(--verdict-skim-bg)', color: 'var(--verdict-skim)' },
};

export default function DimensionRow({ label, data }: DimensionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const jargonStyle = data.jargon_assessment
    ? JARGON_STYLES[data.jargon_assessment]
    : null;

  return (
    <div className={resultsStyles.dimensionRow}>
      <div
        className={resultsStyles.dimensionHeader}
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
      >
        <span className={resultsStyles.dimensionName}>{label}</span>
        <span className={resultsStyles.dimensionChevron}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      <ScoreBar score={data.score} />

      {expanded && (
        <div className={resultsStyles.dimensionExpanded}>
          <p className={resultsStyles.dimensionReasoning}>{data.reasoning}</p>

          {data.flag && (
            <div className={resultsStyles.flagBox}>
              <span className={resultsStyles.flagLabel}>Example from paper</span>
              <p className={resultsStyles.flagText}>"{data.flag}"</p>
            </div>
          )}

          {jargonStyle && (
            <span
              className={resultsStyles.jargonBadge}
              style={{ background: jargonStyle.bg, color: jargonStyle.color }}
            >
              Jargon: {data.jargon_assessment}
            </span>
          )}
        </div>
      )}
    </div>
  );
}